/* ---------- cinema.js — the camera's vocabulary ----------------------------------------------
   Self-contained. Imports three only. Never imports main.js; main.js owns the camera, this owns
   where it should be. Every function is pure: Vector3s in, {pos,target} out. Pass opts.out to
   write into vectors you already own and allocate nothing per frame.

   THE SHOTS — say these words in the host and the code reads like a shot list:

     ESTABLISHING  the opening crane. High, wide, hazed; descends and rotates through ~35° and
                   decelerates onto the fit view over 6s. Three separate curves (sweep / descent /
                   pull-in) so it arcs — a single lerp between two points cannot descend AND turn.
     FIT           the plan view the host already computes (fitPos/fitTarget). Home. Every shot
                   returns here. establishing(bounds,1) lands on it exactly, so orbit control can
                   take over on the next frame without a pop.
     MEET          the two-shot. You stand opposite, they turn to you. Subject on a third, not
                   dead centre. Eye level by default; a hair BELOW eye level for authority seats,
                   so the camera looks up at them.
     FOLLOW        the shoulder. Behind and off-axis, framing the space they are walking INTO,
                   spring-damped, drifting slowly around them, never through the floor.
     ROOM          the corridor look-in. Stand in the hall on the open side, look through the gap.
     DIVE          the roof-off drop. shot(...) with Ease.anticipate — it gathers before it falls.
     ATTRACT       the idle orbit. Nobody has touched it; the floor turns on its own.

   TIMING — real values, tuned, not placeholders:
     establishing 6.0s · dive 1.8s · room 1.1s · meet/return 0.7s (the host's existing default)
--------------------------------------------------------------------------------------------- */
import * as THREE from 'three';

/* ---------- tuned constants ---------- */
const SHOT_DUR      = 0.7;      // host's `tween.dur || 0.7` — changing this retimes every bare tween
const SPRING_K      = 42;       // ω=6.5 rad/s → within 5% in 0.75s, 0.5% in 1.20s: keeps pace, no rubber band
const SPRING_H      = 1/120;    // fixed substep; the host feeds dt up to 0.05 and stiff explicit Euler blows up
const SPRING_MAX_DT = 0.10;     // tab-restore guard

const OPEN_DUR      = 6.0;      // the establishing move
const OPEN_DIST_MUL = 1.55;     // start distance = 1.55× the fit distance — hazed by the host's fog(95,190)
const OPEN_POLAR_MUL= 0.46;     // start polar angle = 46% of fit's → near top-down, then it descends
const OPEN_SWEEP    = 0.62;     // 35.5° of azimuth swept in: enough to read as a move, not enough to disorient
const OPEN_RISE     = 1.4;      // look-at point starts this far above centre, so the horizon drops as you land

const FOLLOW_BACK   = 2.60;     // host's proven shoulder offsets
const FOLLOW_SIDE   = 1.70;
const FOLLOW_EYE    = 1.95;
const FOLLOW_AIM    = 1.35;     // aim height — his shoulders, not his hair
const FOLLOW_AHEAD  = 1.60;     // baseline lead
const FOLLOW_LEAD   = 0.55;     // + speed × this: at a 1.5 u/s walk the frame opens ~0.8 further ahead
const FOLLOW_MIN_Y  = 0.95;     // knee height — the camera never drops through the floor
const FOLLOW_DRIFT_A= 0.17;     // ±9.7° of slow orbital drift around him
const FOLLOW_DRIFT_W= 0.34;     // 18.5s per drift cycle — felt, not seen
const FOLLOW_BOB    = 0.06;     // matching height sway

const MEET_DIST     = 2.20;     // host's meeting distance
const MEET_FOV      = 36;       // host camera's vertical fov
const MEET_ASPECT   = 16/9;     // pass camera.aspect for a true third on any window
const MEET_THIRD    = 1/3;      // subject lands on the third, not the centre
const MEET_DROP     = 0.12;     // authority: camera sits BELOW their eyeline and looks up

const ATTRACT_W     = 0.045;    // 140s per revolution
const ATTRACT_H     = 0.42;     // orbit height as a fraction of radius
const ATTRACT_BREATHE=0.035;    // radius breathes ±3.5%
const ATTRACT_BW    = 0.073;
const ATTRACT_YW    = 0.11;
const ATTRACT_YA    = 0.045;
const ATTRACT_WANDER= 0.90;     // the look-at point wanders so it never feels locked off

const ROOM_STAND    = 1.6;      // host's corridor stand-off
const ROOM_EYE      = 3.1;
const ROOM_LOOK     = 1.0;
const ROOM_DUR      = 1.1;

/* ---------- easing ----------------------------------------------------------------------------
   Every curve satisfies f(0)===0 and f(1)===1 exactly, so a shot always lands on its mark.
   anticipate/overshoot leave [0,1] mid-flight by design; they still hit both endpoints.
--------------------------------------------------------------------------------------------- */
/* the back curves are written factored rather than expanded: (BACK+1)*t - BACK loses the endpoint
   to float error, BACK*(t-1) + t does not. Same polynomial, exact at 0 and 1. */
const BACK = 1.70158;
export const Ease = Object.freeze({
  /* the host's existing curve, bit-identical to `1-Math.pow(1-t,3)` — migration changes nothing */
  linear:     t => t,
  cubicOut:   t => 1 - Math.pow(1 - t, 3),
  /* longer tail than cubic: use where the last 10% of the move should be almost free */
  quintOut:   t => 1 - Math.pow(1 - t, 5),
  /* back-in: pulls away before it commits. The gather before a dive or a reveal */
  anticipate: t => t * t * (BACK * (t - 1) + t),
  /* back-out: arrives past the mark and comes back. Snap for a cut-in, never for a long move */
  overshoot:  t => { const u = t - 1; return 1 + u * u * (BACK * (u + 1) + u); },
  /* one soft overshoot (~5.7% at t≈0.6) then rest. t*t compresses the early frequency so it
     leaves cleanly instead of wobbling off the start. The "camera operator relaxing" curve */
  settle:     t => 1 - Math.pow(1 - t, 3) * Math.cos(t * t * 7.4),
  /* the film curve: zero velocity at BOTH ends, 73% of the move done by half-time, the last 5%
     spent over the final quarter. No jerk on frame one, no pop on handoff to orbit control */
  filmIn:     t => 1 - Math.pow(1 - Math.pow(t, 1.35), 2.6),
});

/* ---------- helpers ---------- */
const isV3 = v => !!(v && v.isVector3);
function pickPos(o){
  if(!o) return null;
  if(o.isVector3) return o;
  return isV3(o.pos) ? o.pos : isV3(o.p) ? o.p : isV3(o.position) ? o.position : null;
}
function pickTgt(o){
  if(!o) return null;
  return isV3(o.target) ? o.target : isV3(o.t) ? o.t : isV3(o.tgt) ? o.tgt : null;
}
function outPair(out){
  return [ (out && out.pos)    || new THREE.Vector3(),
           (out && out.target) || new THREE.Vector3() ];
}

/* ---------- shot: a tween descriptor the host steps ------------------------------------------
   from/to accept {pos,target} · {p,t} · a bare Vector3 (position only). A null `to`, or a `to`
   missing one half, holds that half at `from` — so a descriptor can never produce NaN.
   Unknown opts (thenWalk, …) are carried through untouched: the host still reads tween.thenWalk.
--------------------------------------------------------------------------------------------- */
export function shot(from, to, opts = {}){
  const { dur = SHOT_DUR, ease = Ease.cubicOut, hold = 0, ...rest } = opts;
  const p0 = (pickPos(from) || new THREE.Vector3()).clone();
  const t0 = (pickTgt(from) || new THREE.Vector3()).clone();
  return Object.assign({}, rest, {
    p0, t0,
    p1: (pickPos(to) || p0).clone(),
    t1: (pickTgt(to) || t0).clone(),
    t: 0, held: 0, dur, ease, hold,
    pos: p0.clone(), tgt: t0.clone(),          // descriptor-owned scratch: zero allocation per frame
  });
}

/* ---------- stepTween: advance one frame -----------------------------------------------------
   Returns {pos, target, done}. The vectors belong to the descriptor and are rewritten next
   frame — copy them, don't retain them. Tolerates a raw host literal {p0,t0,t:0} with no p1/t1:
   that holds still rather than going NaN, so a missed migration site fails visibly, not fatally.
--------------------------------------------------------------------------------------------- */
export function stepTween(tw, dt){
  if(!tw) return null;
  if(!tw.pos){ tw.pos = new THREE.Vector3(); tw.tgt = new THREE.Vector3(); }
  const p1 = tw.p1 || tw.p0, t1 = tw.t1 || tw.t0;
  if(tw.hold){                                              // a beat before the camera moves
    tw.held = (tw.held || 0) + dt;
    if(tw.held < tw.hold){
      tw.pos.copy(tw.p0); tw.tgt.copy(tw.t0);
      return { pos: tw.pos, target: tw.tgt, done: false };
    }
  }
  tw.t = (tw.t || 0) + dt / (tw.dur || SHOT_DUR);
  const done = tw.t >= 1;
  const k = (tw.ease || Ease.cubicOut)(done ? 1 : tw.t);
  tw.pos.lerpVectors(tw.p0, p1, k);
  tw.tgt.lerpVectors(tw.t0, t1, k);
  return { pos: tw.pos, target: tw.tgt, done };
}

/* ---------- SpringVec3 -----------------------------------------------------------------------
   Semi-implicit Euler on a fixed 1/120 substep. Critically damped by default (c = 2√k): reaches
   the target fast and never overshoots, which is what you want on a face. Lower the damping for
   a looser, more handheld follow.
--------------------------------------------------------------------------------------------- */
export class SpringVec3 {
  constructor(stiffness = SPRING_K, damping = 2 * Math.sqrt(stiffness)){
    this.k = stiffness; this.c = damping;
    this.value = new THREE.Vector3();
    this.vel   = new THREE.Vector3();
    this._a    = new THREE.Vector3();
  }
  set(v){ this.value.copy(v); this.vel.set(0,0,0); return this; }     // teleport, no snap-back
  step(target, dt){
    let rem = Math.min(dt, SPRING_MAX_DT);
    while(rem > 1e-6){
      const h = Math.min(SPRING_H, rem); rem -= h;
      this._a.copy(target).sub(this.value).multiplyScalar(this.k).addScaledVector(this.vel, -this.c);
      this.vel.addScaledVector(this._a, h);
      this.value.addScaledVector(this.vel, h);
    }
    return this.value;                                               // live vector — copy it
  }
}

/* ---------- followShot: the shoulder ----------------------------------------------------------
   subject: {pos, yaw, speed} — or a rig, in which case root.position / root.rotation.y are read.
   The camera sits behind and to one side; the aim runs AHEAD of him and extends with his speed,
   so the frame holds the space he is walking into, not the space he has left. He never lands on
   the centre line because the camera is off-axis from the aim.
   Drift rotates the camera around him without moving the aim — the framing holds, the angle breathes.
   Pass opts.spring = {pos:SpringVec3, target:SpringVec3} + opts.dt for damping (create once, reuse).
--------------------------------------------------------------------------------------------- */
const _fA = new THREE.Vector3(), _fB = new THREE.Vector3();
export function followShot(subject, opts = {}){
  const root = (subject && subject.root) || subject;
  const p    = pickPos(root) || pickPos(subject) || _fA.set(0,0,0);
  const yaw  = (root && root.rotation ? root.rotation.y : subject.yaw) || 0;
  const {
    back = FOLLOW_BACK, side = FOLLOW_SIDE, height = FOLLOW_EYE, aimHeight = FOLLOW_AIM,
    lookAhead = FOLLOW_AHEAD, leadTime = FOLLOW_LEAD, speed = (subject && subject.speed) || 0,
    minY = FOLLOW_MIN_Y, floorY = 0, time = 0,
    driftAmp = FOLLOW_DRIFT_A, driftRate = FOLLOW_DRIFT_W,
    spring = null, dt = 0, out = null,
  } = opts;

  const a  = yaw + Math.sin(time * driftRate) * driftAmp;   // drifted yaw — camera only
  const fx = Math.sin(a),   fz = Math.cos(a);
  const gx = Math.sin(yaw), gz = Math.cos(yaw);             // true yaw — the aim
  const lead = lookAhead + speed * leadTime;

  _fA.set(p.x - fx*back + fz*side,
          Math.max(floorY + minY, height + Math.sin(time * driftRate * 0.7) * FOLLOW_BOB),
          p.z - fz*back - fx*side);
  _fB.set(p.x + gx*lead, aimHeight, p.z + gz*lead);

  const [oP, oT] = outPair(out);
  if(spring && spring.pos && spring.target && dt > 0){
    oP.copy(spring.pos.step(_fA, dt));
    oT.copy(spring.target.step(_fB, dt));
  } else { oP.copy(_fA); oT.copy(_fB); }
  oP.y = Math.max(floorY + minY, oP.y);                     // the spring can undershoot — the floor cannot
  return { pos: oP, target: oT };
}

/* ---------- meetShot: the two-shot -------------------------------------------------------------
   facingDir points FROM the subject TOWARD the camera (the host already computes this as `dir`).
   The camera stays on that axis — they are looking at you — and the AIM slides sideways, which
   puts the head on a third instead of the crosshair. The slide is computed from the real frame
   width at that distance, so pass camera.fov / camera.aspect and it holds on any window.
   authority:true drops the camera below their eyeline and tilts up. Otherwise: true eye level.
--------------------------------------------------------------------------------------------- */
const _mD = new THREE.Vector3(), _mR = new THREE.Vector3();
export function meetShot(subjectPos, headY, facingDir, opts = {}){
  const { dist = MEET_DIST, fov = MEET_FOV, aspect = MEET_ASPECT, third = MEET_THIRD,
          authority = false, drop = MEET_DROP, side = 1, out = null } = opts;
  _mD.copy(facingDir); _mD.y = 0;
  if(_mD.lengthSq() < 1e-8) _mD.set(0,0,1); else _mD.normalize();
  _mR.set(_mD.z, 0, -_mD.x);                                        // screen-right, level

  const halfW = dist * Math.tan(fov * Math.PI / 360) * aspect;      // world half-width at the subject
  const off   = third * halfW * side;                               // side:+1 puts them on the left third

  const [oP, oT] = outPair(out);
  oP.set(subjectPos.x + _mD.x * dist, headY + (authority ? -drop : 0), subjectPos.z + _mD.z * dist);
  oT.set(subjectPos.x + _mR.x * off,  headY,                          subjectPos.z + _mR.z * off);
  return { pos: oP, target: oT };
}

/* ---------- establishing: the opening crane ----------------------------------------------------
   bounds: { centre, fitPos } — hand it the host's real fitTarget/fitPos, not a re-derived guess.
   t is normalised 0..1 over establishing.DUR seconds.

   Three curves, three axes, one arc:
     azimuth  Ease.settle    over-rotates ~2% then settles — the operator finding the frame
     descent  Ease.quintOut  drops early and long, so the reveal happens while you are still moving
     distance Ease.filmIn    zero velocity at both ends; the pull-in resolves last
   At t>=1 it copies fitPos/centre verbatim, so the handoff to OrbitControls is exact.
--------------------------------------------------------------------------------------------- */
const _eV = new THREE.Vector3();
export function establishing(bounds, t, opts = {}){
  const fit    = pickPos(bounds) || bounds.fitPos;
  const centre = bounds.centre || bounds.center || bounds.fitTarget || pickTgt(bounds);
  const [oP, oT] = outPair(opts.out);
  if(!(t > 0)) t = 0;
  if(t >= 1){ oP.copy(fit); oT.copy(centre); return { pos: oP, target: oT }; }

  _eV.copy(fit).sub(centre);
  const d1  = _eV.length();
  const az1 = Math.atan2(_eV.x, _eV.z);
  const po1 = Math.acos(Math.max(-1, Math.min(1, _eV.y / d1)));
  const d0  = d1 * OPEN_DIST_MUL, az0 = az1 - OPEN_SWEEP, po0 = po1 * OPEN_POLAR_MUL;

  const d  = d0  + (d1  - d0 ) * Ease.filmIn(t);
  const az = az0 + (az1 - az0) * Ease.settle(t);
  const po = po0 + (po1 - po0) * Ease.quintOut(t);
  const sp = Math.sin(po);

  oP.set(centre.x + d*sp*Math.sin(az), centre.y + d*Math.cos(po), centre.z + d*sp*Math.cos(az));
  oT.copy(centre); oT.y += OPEN_RISE * (1 - Ease.cubicOut(t));       // the horizon drops as you land
  return { pos: oP, target: oT };
}
establishing.DUR = OPEN_DUR;

/* ---------- attractDrift: the idle orbit -------------------------------------------------------
   t is absolute seconds, so it is stateless: call it every frame while nobody has interacted and
   blend out the moment they do. Radius breathes, height sways, the look-at wanders — three
   incommensurate periods, so it never visibly repeats.
--------------------------------------------------------------------------------------------- */
export function attractDrift(centre, t, radius, opts = {}){
  const { height = ATTRACT_H, rate = ATTRACT_W, out = null } = opts;
  const [oP, oT] = outPair(out);
  const az = t * rate;
  const r  = radius * (1 + ATTRACT_BREATHE * Math.sin(t * ATTRACT_BW));
  oP.set(centre.x + r * Math.sin(az),
         centre.y + radius * height + Math.sin(t * ATTRACT_YW) * radius * ATTRACT_YA,
         centre.z + r * Math.cos(az));
  oT.set(centre.x + Math.sin(t * 0.037) * ATTRACT_WANDER,
         centre.y,
         centre.z + Math.cos(t * 0.029) * ATTRACT_WANDER);
  return { pos: oP, target: oT };
}

/* ---------- frameRoom: the corridor look-in ----------------------------------------------------
   rect is the host's format verbatim — [x0, z0, x1, z1] — so ROOMS entries pass straight through.
   North rooms (rect[3] <= 0) open onto the corridor from their far edge; south rooms from their
   near edge. Override with opts.side = 'north' | 'south' for a room that reads the other way.
--------------------------------------------------------------------------------------------- */
export function frameRoom(rect, opts = {}){
  const { standOff = ROOM_STAND, eye = ROOM_EYE, look = ROOM_LOOK, side = null, out = null } = opts;
  const cx = (rect[0] + rect[2]) / 2, cz = (rect[1] + rect[3]) / 2;
  const north = side ? side === 'north' : rect[3] <= 0;
  const [oP, oT] = outPair(out);
  oP.set(cx, eye, north ? rect[3] + standOff : rect[1] - standOff);
  oT.set(cx, look, cz);
  return { pos: oP, target: oT };
}
frameRoom.DUR = ROOM_DUR;
